import {
  Building2,
  Calendar,
  DollarSign,
  Clock,
  Truck,
  Trophy,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { Bid } from '../../types';

interface DetailsTabProps {readonly bid: Bid;}

export function DetailsTab({ bid }: DetailsTabProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: bid.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Bid Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Event</p>
                <p className="font-medium">{bid.eventNumber}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Total Bid Amount</p>
                <p className={cn('font-medium text-lg', bid.isLowestBid && 'text-green-600')}>
                  {formatCurrency(bid.total)}
                </p>
              </div>
            </div>
            {bid.validUntil && (
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Valid Until</p>
                  <p className="font-medium">{formatDate(bid.validUntil)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Supplier Details</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Supplier</p>
                <p className="font-medium">{bid.supplierName}</p>
              </div>
            </div>
            {bid.deliveryTerms && (
              <div className="flex items-center gap-3">
                <Truck className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Delivery Terms</p>
                  <p className="font-medium">{bid.deliveryTerms}</p>
                </div>
              </div>
            )}
            {bid.submittedAt && (
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Submitted</p>
                  <p className="font-medium">{formatDate(bid.submittedAt)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {bid.isLowestBid && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4">
          <div className="flex items-center gap-3">
            <Trophy className="h-5 w-5 text-green-500" />
            <p className="font-medium text-green-800">
              This is the lowest bid for this sourcing event
            </p>
          </div>
        </div>
      )}

      {bid.notes && (
        <div className="rounded-lg bg-gray-50 border p-4">
          <p className="text-sm text-gray-500 mb-1">Supplier Notes</p>
          <p className="text-gray-700">{bid.notes}</p>
        </div>
      )}

      {bid.status === 'accepted' && bid.reviewedByName && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
            <div>
              <p className="font-medium text-green-800">Accepted by {bid.reviewedByName}</p>
              {bid.reviewedAt && <p className="text-sm text-green-700">{formatDate(bid.reviewedAt)}</p>}
            </div>
          </div>
        </div>
      )}

      {bid.status === 'rejected' && bid.reviewedByName && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Rejected by {bid.reviewedByName}</p>
              {bid.reviewedAt && <p className="text-sm text-red-700">{formatDate(bid.reviewedAt)}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
